import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { auth, hasFirebaseConfig } from "../lib/firebase";
import {
  loadUserProfile,
  readAdminClaims,
  touchAdminLogin,
} from "../services/adminAccountService";
import { writeAuditEvent } from "../services/auditService";
import type { AdminClaims, AppUserProfile } from "../types/auth";
import { normalizeEmail } from "../utils/firestoreData";

type AuthErrorCode =
  | "configuration"
  | "login_failed"
  | "missing_profile"
  | "not_admin"
  | "reset_failed"
  | "signout_failed";

type AuthContextValue = {
  claims: AdminClaims;
  errorCode: AuthErrorCode | null;
  errorMessage: string | null;
  isAdmin: boolean;
  isConfigured: boolean;
  isGlobalAdmin: boolean;
  isLoading: boolean;
  profile: AppUserProfile | null;
  resetPassword: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  user: User | null;
};

const defaultClaims: AdminClaims = {
  globalAdmin: false,
  canCreateFormation: false,
  canCreateSession: false,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function profileHasAdminAccess(
  profile: AppUserProfile | null,
  claims: AdminClaims,
): boolean {
  if (claims.globalAdmin) {
    return true;
  }

  return (
    profile?.accountType === "admin" &&
    profile.status === "active" &&
    profile.disabledAt === null
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<AdminClaims>(defaultClaims);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(hasFirebaseConfig);
  const [profile, setProfile] = useState<AppUserProfile | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!auth || !hasFirebaseConfig) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setUser(nextUser);
      setErrorCode(null);
      setErrorMessage(null);

      void (async () => {
        if (!nextUser) {
          if (requestId !== requestIdRef.current) {
            return;
          }

          setClaims(defaultClaims);
          setProfile(null);
          setIsLoading(false);
          return;
        }

        try {
          const tokenResult = await nextUser.getIdTokenResult(true);
          const nextClaims = readAdminClaims(tokenResult.claims);
          const nextProfile =
            (await touchAdminLogin(nextUser)) ??
            (await loadUserProfile(nextUser.uid));

          if (requestId !== requestIdRef.current) {
            return;
          }

          setClaims(nextClaims);
          setProfile(nextProfile);

          if (!profileHasAdminAccess(nextProfile, nextClaims)) {
            setErrorCode(nextProfile ? "not_admin" : "missing_profile");
            setErrorMessage(
              nextProfile
                ? "Ce compte ne dispose pas d'un accès administrateur actif."
                : "Ce compte existe dans Firebase Auth, mais aucun profil administrateur /users n'est encore associé.",
            );
          }

          setIsLoading(false);
        } catch (error) {
          if (requestId !== requestIdRef.current) {
            return;
          }

          setClaims(defaultClaims);
          setProfile(null);
          setErrorCode("login_failed");
          setErrorMessage(
            getErrorMessage(
              error,
              "Impossible de vérifier les droits administrateur.",
            ),
          );
          setIsLoading(false);
        }
      })();
    });

    return () => {
      requestIdRef.current += 1;
      unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!auth || !hasFirebaseConfig) {
      setErrorCode("configuration");
      setErrorMessage("La configuration Firebase est incomplète.");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);
    setErrorMessage(null);

    try {
      await signInWithEmailAndPassword(auth, normalizeEmail(email), password);
    } catch (error) {
      setIsLoading(false);
      setErrorCode("login_failed");
      setErrorMessage(
        getErrorMessage(error, "Connexion impossible avec ces identifiants."),
      );
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!auth || !hasFirebaseConfig) {
      setErrorCode("configuration");
      setErrorMessage("La configuration Firebase est incomplète.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, normalizeEmail(email), {
        url: window.location.origin,
      });
      setErrorCode(null);
      setErrorMessage("Email de réinitialisation envoyé si le compte existe.");
    } catch (error) {
      setErrorCode("reset_failed");
      setErrorMessage(
        getErrorMessage(error, "Impossible d'envoyer la réinitialisation."),
      );
    }
  }, []);

  const signOutUser = useCallback(async () => {
    if (!auth) {
      return;
    }

    try {
      const actorUid = auth.currentUser?.uid ?? null;
      await signOut(auth);
      await writeAuditEvent({
        actorUid,
        eventType: "admin.logout",
      });
    } catch (error) {
      setErrorCode("signout_failed");
      setErrorMessage(getErrorMessage(error, "Déconnexion impossible."));
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const isAdmin = profileHasAdminAccess(profile, claims);

    return {
      claims,
      errorCode,
      errorMessage,
      isAdmin,
      isConfigured: hasFirebaseConfig,
      isGlobalAdmin: claims.globalAdmin || profile?.role === "global_admin",
      isLoading,
      profile,
      resetPassword,
      signIn,
      signOutUser,
      user,
    };
  }, [
    claims,
    errorCode,
    errorMessage,
    isLoading,
    profile,
    resetPassword,
    signIn,
    signOutUser,
    user,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider.");
  }

  return context;
}
